import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, photoUrl } from "@/lib/api";
import { toast, confirmDialog } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import {
  Check, X, Trash2, Users, FileCheck, BarChart3, Settings, BadgeCheck, Eye,
  Flag, ShieldCheck, CreditCard, Search, ChevronLeft, ChevronRight, Sparkles,
  Loader2, Zap, Star, Globe, Crown, UserPlus, Link as LinkIcon, Copy, ShieldPlus,
} from "lucide-react";

const PAGE_SIZE = 20;

export default function Admin() {
  const [tab, setTab] = useState("queue");
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState({ items: [], total: 0 });
  const [err, setErr] = useState("");
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const loadQueue = useCallback(async () => {
    try {
      const [s, q] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/profiles?status=pending&page=1&page_size=${PAGE_SIZE}`),
      ]);
      setStats(s.data);
      setQueue(q.data);
    } catch (e) { setErr(formatApiError(e)); }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const impersonate = async (userId, label) => {
    const ok = await confirmDialog({
      title: `Vis platformen som ${label || "denne bruger"}?`,
      body: "Du kan stoppe når som helst med et enkelt klik.",
      confirmText: "Ja, log ind som bruger",
      cancelText: "Annullér",
    });
    if (!ok) return;
    try {
      await api.post(`/admin/impersonate/${userId}`);
      await refresh();
      navigate("/matches");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7" data-testid="admin-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="eyebrow">Administration</div>
          <h1 className="mt-2 font-serif text-4xl text-olive-700">Admin-panel</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/dns" className="btn-secondary" data-testid="open-dns-link">
            <Globe className="h-4 w-4" /> DNS-opsætning
          </Link>
          <Link to="/admin/settings" className="btn-secondary" data-testid="open-settings-link">
            <Settings className="h-4 w-4" /> Indstillinger
          </Link>
        </div>
      </div>
      {err && <div className="mt-3 text-destructive text-sm">{err}</div>}

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Brugere" value={stats.total_users} />
          <Stat label="Afventer" value={stats.pending_profiles} accent="gold" />
          <Stat label="Godkendt" value={stats.approved_profiles} />
          <Stat label="Medlemmer" value={stats.active_subscribers} />
          <Stat label="Matches" value={stats.total_matches} />
        </div>
      )}

      <div className="mt-10 inline-flex flex-wrap gap-1 rounded-full border border-sand-300 p-1 bg-white max-w-full">
        <AdminTab active={tab === "queue"} onClick={() => setTab("queue")} testid="tab-queue"><FileCheck className="h-4 w-4" /> Kø ({queue.total})</AdminTab>
        <AdminTab active={tab === "profiles"} onClick={() => setTab("profiles")} testid="tab-profiles"><Users className="h-4 w-4" /> Profiler</AdminTab>
        <AdminTab active={tab === "users"} onClick={() => setTab("users")} testid="tab-users"><BarChart3 className="h-4 w-4" /> Brugere</AdminTab>
        <AdminTab active={tab === "reports"} onClick={() => setTab("reports")} testid="tab-reports"><Flag className="h-4 w-4" /> Rapporter</AdminTab>
        <AdminTab active={tab === "verifications"} onClick={() => setTab("verifications")} testid="tab-verifications"><ShieldCheck className="h-4 w-4" /> Verificeringer</AdminTab>
        <AdminTab active={tab === "subscriptions"} onClick={() => setTab("subscriptions")} testid="tab-subscriptions"><CreditCard className="h-4 w-4" /> Abonnementer</AdminTab>
        <AdminTab active={tab === "demo"} onClick={() => setTab("demo")} testid="tab-demo"><Sparkles className="h-4 w-4" /> Demo-data</AdminTab>
        <AdminTab active={tab === "team"} onClick={() => setTab("team")} testid="tab-team"><ShieldPlus className="h-4 w-4" /> Adgang & Team</AdminTab>
      </div>

      <div className="mt-8">
        {tab === "queue" && <ProfilesPanel key="queue" fixedStatus="pending" onImpersonate={impersonate} refreshStats={loadQueue} />}
        {tab === "profiles" && <ProfilesPanel key="profiles" onImpersonate={impersonate} refreshStats={loadQueue} />}
        {tab === "users" && <UsersPanel onImpersonate={impersonate} />}
        {tab === "reports" && <ReportsTab />}
        {tab === "verifications" && <VerificationsTab />}
        {tab === "subscriptions" && <SubscriptionsTab />}
        {tab === "demo" && <DemoDataPanel onRefresh={loadQueue} />}
        {tab === "team" && <TeamPanel />}
      </div>
    </div>
  );
}

// ---------- Profiles panel (queue + all) with pagination ----------
function ProfilesPanel({ fixedStatus, onImpersonate, refreshStats }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [gender, setGender] = useState("all");
  const [verified, setVerified] = useState("all");
  const [status, setStatus] = useState(fixedStatus || "all");
  const [isDemo, setIsDemo] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (status !== "all") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      if (gender !== "all") params.set("gender", gender);
      if (verified !== "all") params.set("verified", verified);
      if (isDemo !== "all") params.set("is_demo", isDemo);
      const { data } = await api.get(`/admin/profiles?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [page, q, gender, verified, status, isDemo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, gender, verified, status, isDemo]);

  const act = async (id, action, reason) => {
    try { await api.post(`/admin/profiles/${id}/action`, { action, reason }); await load(); refreshStats?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!(await confirmDialog({ title: "Slet denne profil permanent?", body: "Handlingen kan ikke fortrydes.", tone: "danger", confirmText: "Slet profil" }))) return;
    try { await api.delete(`/admin/profiles/${id}`); await load(); refreshStats?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const verify = async (id, currentlyVerified) => {
    try {
      if (currentlyVerified) await api.delete(`/admin/profiles/${id}/verify`);
      else await api.post(`/admin/profiles/${id}/verify`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <FilterBar>
        <SearchInput value={q} onChange={setQ} placeholder="Søg navn, by, e-mail…" testid="profiles-search" />
        {!fixedStatus && (
          <Select value={status} onChange={setStatus} testid="profiles-filter-status" label="Status">
            <option value="all">Alle</option>
            <option value="draft">Kladde</option>
            <option value="pending">Afventer</option>
            <option value="approved">Godkendt</option>
            <option value="rejected">Afvist</option>
          </Select>
        )}
        <Select value={gender} onChange={setGender} testid="profiles-filter-gender" label="Køn">
          <option value="all">Alle</option>
          <option value="male">Mænd</option>
          <option value="female">Kvinder</option>
        </Select>
        <Select value={verified} onChange={setVerified} testid="profiles-filter-verified" label="Verificeret">
          <option value="all">Alle</option>
          <option value="yes">Kun verificerede</option>
          <option value="no">Ikke verificeret</option>
        </Select>
        <Select value={isDemo} onChange={setIsDemo} testid="profiles-filter-demo" label="Demo">
          <option value="all">Alle</option>
          <option value="yes">Kun demo</option>
          <option value="no">Uden demo</option>
        </Select>
      </FilterBar>

      <ProfilesTable
        items={items}
        loading={loading}
        onApprove={(p) => act(p.id, "approve")}
        onReject={(p) => { const r = prompt("Årsag?", ""); if (r != null) act(p.id, "reject", r); }}
        onDelete={(p) => del(p.id)}
        onVerify={(p) => verify(p.id, p.verified)}
        onImpersonate={(p) => onImpersonate(p.user_id, p.display_name)}
      />

      <Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} />
    </div>
  );
}

// ---------- Users panel with search + pagination ----------
function UsersPanel({ onImpersonate }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");
  const [gender, setGender] = useState("all");
  const [boosted, setBoosted] = useState("all");
  const [isDemo, setIsDemo] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (tier !== "all") params.set("filter", tier);
      if (q.trim()) params.set("q", q.trim());
      if (gender !== "all") params.set("gender", gender);
      if (boosted !== "all") params.set("boosted", boosted);
      if (isDemo !== "all") params.set("is_demo", isDemo);
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [page, q, tier, gender, boosted, isDemo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, tier, gender, boosted, isDemo]);

  const togglePremium = async (u) => {
    const isPremium = !!u.subscription?.active;
    if (isPremium) {
      const ok = await confirmDialog({
        title: "Fjern premium?",
        body: `Fjern premium-adgang for ${u.name || u.email}?`,
        confirmText: "Fjern premium", cancelText: "Annullér",
      });
      if (!ok) return;
      try { await api.delete(`/admin/users/${u.id}/premium`); toast.success("Premium fjernet"); load(); }
      catch (e) { toast.error(formatApiError(e)); }
    } else {
      const ok = await confirmDialog({
        title: "Gør til premium?",
        body: `Giv ${u.name || u.email} livstids-premium? (Du kan lave tidsbegrænset premium via invitationslinks.)`,
        confirmText: "Gør premium", cancelText: "Annullér",
      });
      if (!ok) return;
      try { await api.post(`/admin/users/${u.id}/premium`, { premium_months: null }); toast.success("Premium tildelt (livstid)"); load(); }
      catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <FilterBar>
        <SearchInput value={q} onChange={setQ} placeholder="Søg navn eller e-mail…" testid="users-search" />
        <Select value={tier} onChange={setTier} testid="user-filter" label="Niveau">
          <option value="all">Alle</option>
          <option value="unsubscribed">Gratis</option>
          <option value="subscribed">Betalende</option>
          <option value="monthly">Månedlig</option>
          <option value="full_package">Fuld pakke</option>
        </Select>
        <Select value={gender} onChange={setGender} testid="users-filter-gender" label="Køn">
          <option value="all">Alle</option>
          <option value="male">Mænd</option>
          <option value="female">Kvinder</option>
        </Select>
        <Select value={boosted} onChange={setBoosted} testid="users-filter-boost" label="Boost">
          <option value="all">Alle</option>
          <option value="yes">Kun boostede</option>
        </Select>
        <Select value={isDemo} onChange={setIsDemo} testid="users-filter-demo" label="Demo">
          <option value="all">Alle</option>
          <option value="yes">Kun demo</option>
          <option value="no">Uden demo</option>
        </Select>
      </FilterBar>

      {loading && <div className="card-soft p-6 text-center text-brown-500 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div>}

      <div className="card-soft overflow-x-auto hidden lg:block">
        <table className="w-full text-sm">
          <thead className="bg-sand-100 text-brown-500 text-left">
            <tr>
              <Th>Bruger</Th><Th>E-mail</Th><Th>Køn</Th><Th>Niveau</Th><Th>Matches</Th><Th>Beskeder</Th><Th>Tilmeldt</Th><Th className="text-right">Handling</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-sand-200" data-testid={`user-row-${u.id}`}>
                <Td>
                  <div className="flex items-center gap-2">
                    <Avatar user={u} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="font-medium truncate">{u.profile_summary?.display_name || u.name}</span>
                        {u.profile_summary?.verified && <BadgeCheck className="h-3.5 w-3.5 text-sky-600 fill-sky-100 shrink-0" />}
                        {u.boost_active && <Zap className="h-3.5 w-3.5 text-gold-600 shrink-0" title="Boostet" />}
                        {u.is_demo && <span className="text-[9px] uppercase tracking-widest bg-purple-100 text-purple-700 rounded px-1.5 py-0.5">Demo</span>}
                      </div>
                      {u.profile_summary?.city && (
                        <div className="text-[11px] text-brown-500 truncate">{u.profile_summary.city}, {u.profile_summary.country}</div>
                      )}
                    </div>
                  </div>
                </Td>
                <Td className="break-all text-xs">{u.email}</Td>
                <Td className="capitalize text-xs">{u.profile_summary?.gender === "male" ? "mand" : u.profile_summary?.gender === "female" ? "kvinde" : "—"}</Td>
                <Td>{tierBadge(u)}</Td>
                <Td>{u.match_count}</Td>
                <Td>{u.message_count}</Td>
                <Td>{new Date(u.created_at).toLocaleDateString("da-DK")}</Td>
                <td className="px-4 py-3 text-right">
                  {u.role !== "admin" && (
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => togglePremium(u)} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium ${u.subscription?.active ? "border-brown-300 text-brown-600 hover:bg-sand-100" : "border-olive-500/40 text-olive-700 hover:bg-olive-100/60"}`} data-testid={`premium-toggle-${u.id}`}>
                        <Crown className="h-3.5 w-3.5" /> {u.subscription?.active ? "Fjern premium" : "Gør premium"}
                      </button>
                      <button onClick={() => onImpersonate(u.id, u.name || u.email)} className="inline-flex items-center gap-1 rounded-full border border-gold-500/40 px-3 py-1.5 text-gold-700 hover:bg-gold-100/60 text-xs font-medium" data-testid={`impersonate-user-${u.id}`}>
                        <Eye className="h-3.5 w-3.5" /> Vis som bruger
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr><td colSpan={8} className="p-10 text-center text-brown-500">Ingen brugere matcher.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile user cards */}
      <ul className="space-y-3 lg:hidden">
        {items.map((u) => (
          <li key={u.id} className="card-soft p-5" data-testid={`user-row-${u.id}`}>
            <div className="flex items-start gap-3">
              <Avatar user={u} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="font-serif text-lg text-olive-700 truncate flex items-center gap-1">
                  {u.profile_summary?.display_name || u.name}
                  {u.profile_summary?.verified && <BadgeCheck className="h-4 w-4 text-sky-600 fill-sky-100 shrink-0" />}
                  {u.boost_active && <Zap className="h-4 w-4 text-gold-600 shrink-0" />}
                </div>
                <div className="text-xs text-brown-500 break-all">{u.email}</div>
                {u.is_demo && <span className="text-[9px] uppercase tracking-widest bg-purple-100 text-purple-700 rounded px-1.5 py-0.5 mt-1 inline-block">Demo</span>}
              </div>
              {tierBadge(u)}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="bg-sand-100 rounded-xl py-2">
                <div className="text-xs text-brown-500">Matches</div>
                <div className="font-serif text-lg text-olive-700">{u.match_count}</div>
              </div>
              <div className="bg-sand-100 rounded-xl py-2">
                <div className="text-xs text-brown-500">Beskeder</div>
                <div className="font-serif text-lg text-olive-700">{u.message_count}</div>
              </div>
              <div className="bg-sand-100 rounded-xl py-2">
                <div className="text-xs text-brown-500">Tilmeldt</div>
                <div className="font-serif text-sm text-olive-700 pt-1">{new Date(u.created_at).toLocaleDateString("da-DK")}</div>
              </div>
            </div>
            {u.role !== "admin" && (
              <div className="mt-4 pt-4 border-t border-sand-200 flex flex-wrap gap-2">
                <button onClick={() => togglePremium(u)} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs ${u.subscription?.active ? "border-brown-300 text-brown-600" : "border-olive-500/40 text-olive-700"}`} data-testid={`premium-toggle-mobile-${u.id}`}>
                  <Crown className="h-3.5 w-3.5" /> {u.subscription?.active ? "Fjern premium" : "Gør premium"}
                </button>
                <button onClick={() => onImpersonate(u.id, u.name || u.email)} className="inline-flex items-center gap-1 rounded-full border border-gold-500/40 px-3 py-1.5 text-gold-700 hover:bg-gold-100/60 text-xs" data-testid={`impersonate-user-mobile-${u.id}`}>
                  <Eye className="h-3.5 w-3.5" /> Vis som bruger
                </button>
              </div>
            )}
          </li>
        ))}
        {items.length === 0 && !loading && <li className="card-soft p-10 text-center text-brown-500">Ingen brugere matcher.</li>}
      </ul>

      <Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} />
    </div>
  );
}

function tierBadge(u) {
  if (u.subscription?.active) {
    return (
      <span className="inline-flex items-center gap-1 text-xs bg-olive-100 text-olive-700 px-2 py-0.5 rounded-full capitalize whitespace-nowrap">
        <Star className="h-3 w-3" /> {(u.subscription.tier || "-").replaceAll("_", " ")}
      </span>
    );
  }
  return <span className="text-xs bg-sand-200 text-brown-500 px-2 py-0.5 rounded-full whitespace-nowrap">Gratis</span>;
}

function Avatar({ user, size = "sm" }) {
  const px = size === "lg" ? "h-12 w-12" : "h-9 w-9";
  const photos = user?.profile_summary?.photos || [];
  const idx = user?.profile_summary?.main_photo_index || 0;
  const src = photos[idx] ? photoUrl(photos[idx]) : "";
  if (src) {
    return <img loading="lazy" src={src} alt="" className={`${px} rounded-full object-cover border border-sand-300 shrink-0`} />;
  }
  const initials = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();
  return (
    <div className={`${px} rounded-full bg-olive-100 text-olive-700 flex items-center justify-center font-medium border border-sand-300 shrink-0`}>
      {initials}
    </div>
  );
}

// ---------- Reusable UI bits ----------
function FilterBar({ children }) {
  return <div className="flex flex-wrap items-end gap-2 mb-4">{children}</div>;
}

function SearchInput({ value, onChange, placeholder, testid }) {
  return (
    <label className="flex-1 min-w-[220px]">
      <div className="text-[10px] uppercase tracking-widest text-brown-500 mb-1">Søg</div>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brown-400" />
        <input
          value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="field pl-9 w-full"
          data-testid={testid}
        />
      </div>
    </label>
  );
}

function Select({ value, onChange, children, testid, label }) {
  return (
    <label className="min-w-[140px]">
      <div className="text-[10px] uppercase tracking-widest text-brown-500 mb-1">{label}</div>
      <select className="field w-full" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        {children}
      </select>
    </label>
  );
}

function Pagination({ page, totalPages, total, onChange }) {
  if (total <= PAGE_SIZE) return <div className="mt-4 text-xs text-brown-400 text-right">{total} resultat{total === 1 ? "" : "er"}</div>;
  return (
    <div className="mt-6 flex items-center justify-between gap-3 flex-wrap" data-testid="pagination">
      <div className="text-xs text-brown-500">
        Side <strong>{page}</strong> af <strong>{totalPages}</strong> · <span className="text-brown-400">{total} i alt</span>
      </div>
      <div className="flex gap-1">
        <button
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="inline-flex items-center gap-1 rounded-full border border-sand-300 px-3 py-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed hover:bg-sand-100"
          data-testid="page-prev">
          <ChevronLeft className="h-3.5 w-3.5" /> Forrige
        </button>
        <button
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          className="inline-flex items-center gap-1 rounded-full border border-sand-300 px-3 py-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed hover:bg-sand-100"
          data-testid="page-next">
          Næste <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`card-soft p-5 ${accent === "gold" ? "bg-gold-100/60 border-gold-500/30" : ""}`}>
      <div className="text-xs uppercase tracking-wider text-brown-500">{label}</div>
      <div className="mt-2 font-serif text-3xl text-olive-700">{value}</div>
    </div>
  );
}

function AdminTab({ children, active, onClick, testid }) {
  return (
    <button onClick={onClick} className={`px-5 py-2 rounded-full text-sm inline-flex items-center gap-2 transition ${active ? "bg-olive-500 text-white" : "text-brown-600 hover:bg-sand-100"}`} data-testid={testid}>
      {children}
    </button>
  );
}

function Th({ children }) { return <th className="px-4 py-3 font-medium whitespace-nowrap">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 text-brown-600 align-top ${className}`}>{children}</td>; }

function ProfileActions({ p, onApprove, onReject, onDelete, onVerify, onImpersonate }) {
  return (
    <div className="flex flex-wrap gap-2">
      {p.status === "approved" && (
        <button onClick={() => onVerify(p)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs border ${p.verified ? "bg-sky-100 text-sky-700 border-sky-500/40" : "bg-white text-olive-600 border-olive-500/30 hover:bg-olive-50"}`} data-testid={`verify-${p.id}`}>
          <BadgeCheck className="h-3.5 w-3.5" /> {p.verified ? "Verificeret" : "Verificér"}
        </button>
      )}
      {p.status !== "approved" && (
        <button onClick={() => onApprove(p)} className="btn-primary py-1.5 px-3 text-xs" data-testid={`approve-${p.id}`}><Check className="h-3.5 w-3.5" /> Godkend</button>
      )}
      {p.status !== "rejected" && (
        <button onClick={() => onReject(p)} className="btn-secondary py-1.5 px-3 text-xs" data-testid={`reject-${p.id}`}><X className="h-3.5 w-3.5" /> Afvis</button>
      )}
      {onImpersonate && p.user_id && (
        <button onClick={() => onImpersonate(p)} className="inline-flex items-center gap-1 rounded-full border border-gold-500/40 px-3 py-1.5 text-gold-700 hover:bg-gold-100/60 text-xs font-medium" data-testid={`impersonate-${p.id}`} title="Vis platformen som denne bruger">
          <Eye className="h-3.5 w-3.5" /> Vis som bruger
        </button>
      )}
      <button onClick={() => onDelete(p)} className="inline-flex items-center gap-1 rounded-full border border-destructive/40 px-3 py-1.5 text-destructive hover:bg-destructive/10 text-xs" data-testid={`delete-profile-${p.id}`}><Trash2 className="h-3.5 w-3.5" /> Slet</button>
    </div>
  );
}

function ProfilesTable({ items, loading, onApprove, onReject, onDelete, onVerify, onImpersonate }) {
  if (loading) {
    return <div className="card-soft p-6 text-center text-brown-500 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div>;
  }
  if (!items || items.length === 0) {
    return (
      <div className="card-soft p-10 text-center text-brown-500" data-testid="empty-profiles-state">
        <div className="font-serif text-xl text-olive-700 mb-2">Ingen profiler matcher</div>
        <p className="text-sm">Justér filtre eller søgeord.</p>
      </div>
    );
  }

  return (
    <>
      <ul className="space-y-3 lg:hidden" data-testid="profiles-mobile-list">
        {items.map((p) => (
          <li key={p.id} className="card-soft p-5" data-testid={`profile-row-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-serif text-xl text-olive-700 truncate flex items-center gap-1.5">
                  {p.display_name || "—"}
                  {p.verified && <BadgeCheck className="h-4 w-4 text-sky-600 fill-sky-100 shrink-0" />}
                  {p.is_demo && <span className="text-[9px] uppercase tracking-widest bg-purple-100 text-purple-700 rounded px-1.5 py-0.5">Demo</span>}
                </div>
                <div className="text-xs text-brown-500 capitalize">{p.gender === "male" ? "mand" : p.gender === "female" ? "kvinde" : p.gender}</div>
              </div>
              <StatusBadge status={p.status} />
            </div>
            <dl className="mt-4 space-y-1.5 text-sm">
              <Row label="Ejer"><span className="break-all">{p.owner?.email || "—"}</span></Row>
              <Row label="By">{[p.city, p.country].filter(Boolean).join(", ") || "—"}</Row>
              <Row label="Opdateret">{p.updated_at ? new Date(p.updated_at).toLocaleDateString("da-DK") : "—"}</Row>
              {p.rejection_reason && <Row label="Årsag"><span className="text-destructive">{p.rejection_reason}</span></Row>}
            </dl>
            <div className="mt-4 pt-4 border-t border-sand-200">
              <ProfileActions p={p} onApprove={onApprove} onReject={onReject} onDelete={onDelete} onVerify={onVerify} onImpersonate={onImpersonate} />
            </div>
          </li>
        ))}
      </ul>

      <div className="card-soft overflow-x-auto hidden lg:block">
        <table className="w-full text-sm">
          <thead className="bg-sand-100 text-brown-500 text-left">
            <tr>
              <Th>Navn</Th><Th>Ejer</Th><Th>Status</Th><Th>By</Th><Th>Opdateret</Th><Th className="text-right">Handlinger</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-sand-200">
                <Td>
                  <span className="inline-flex items-center gap-1">
                    {p.display_name}
                    {p.verified && <BadgeCheck className="h-4 w-4 text-sky-600 fill-sky-100" />}
                    {p.is_demo && <span className="text-[9px] uppercase tracking-widest bg-purple-100 text-purple-700 rounded px-1.5 py-0.5 ml-1">Demo</span>}
                  </span>
                  <span className="text-brown-500">, {p.gender === "male" ? "mand" : p.gender === "female" ? "kvinde" : p.gender}</span>
                </Td>
                <Td className="break-all">{p.owner?.email || "—"}</Td>
                <Td><StatusBadge status={p.status} /></Td>
                <Td>{[p.city, p.country].filter(Boolean).join(", ")}</Td>
                <Td>{new Date(p.updated_at).toLocaleDateString("da-DK")}</Td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex"><ProfileActions p={p} onApprove={onApprove} onReject={onReject} onDelete={onDelete} onVerify={onVerify} onImpersonate={onImpersonate} /></div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-2">
      <dt className="text-brown-500 text-xs uppercase tracking-wider min-w-[72px] pt-0.5">{label}</dt>
      <dd className="text-brown-600 flex-1 min-w-0">{children}</dd>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-gold-100 text-gold-600 border-gold-500/40",
    approved: "bg-olive-100 text-olive-600 border-olive-500/40",
    rejected: "bg-destructive/10 text-destructive border-destructive/40",
    draft: "bg-sand-200 text-brown-600 border-sand-300",
  };
  const labelMap = { pending: "afventer", approved: "godkendt", rejected: "afvist", draft: "kladde" };
  return <span className={`px-2 py-0.5 rounded-full border text-xs capitalize ${map[status] || ""}`}>{labelMap[status] || status}</span>;
}

// ---------- Reports tab ----------
function ReportsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = async () => {
    try { const { data } = await api.get(`/admin/reports?status=${status}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [status]);
  const resolve = async (id, action) => {
    const note = action === "dismiss" ? "" : prompt("Note (valgfri)?", "") || "";
    try { await api.post(`/admin/reports/${id}/resolve`, { action, note }); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const REASON = { fake_profile: "Falsk profil", harassment: "Chikane", inappropriate: "Upassende", scam: "Svindel", married: "Gift", spam: "Spam", underage: "Mindreårig", other: "Andet" };
  return (
    <div data-testid="reports-tab">
      <div className="flex gap-2 mb-4">
        {["pending", "action_taken", "dismissed"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1 text-xs rounded-full ${status === s ? "bg-olive-500 text-white" : "bg-sand-100 text-brown-600"}`} data-testid={`reports-filter-${s}`}>{s}</button>
        ))}
      </div>
      {items.length === 0 && <div className="card-soft p-10 text-center text-brown-500">Ingen rapporter.</div>}
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="card-soft p-4" data-testid={`report-row-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm text-brown-500"><strong>{REASON[r.reason] || r.reason}</strong> · {new Date(r.created_at).toLocaleString("da-DK")}</div>
                <div className="mt-1 text-sm">
                  <span className="text-brown-500">Rapporteret af:</span> {r.reporter?.name || r.reporter?.email || r.reporter_id}
                  <span className="text-brown-500 mx-2">→</span>
                  <span className="text-brown-500">Rapporterede:</span> {r.reported?.name || r.reported?.email || r.reported_id}
                </div>
                {r.details && <div className="mt-2 text-xs text-brown-600 bg-sand-100 rounded p-2 whitespace-pre-wrap">{r.details}</div>}
                <div className="mt-2 text-[10px] uppercase tracking-widest text-brown-400">Status: {r.status}</div>
              </div>
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <button onClick={() => resolve(r.id, "dismiss")} className="btn-ghost text-xs" data-testid={`dismiss-${r.id}`}>Afvis</button>
                  <button onClick={() => resolve(r.id, "warn")} className="btn-secondary text-xs" data-testid={`warn-${r.id}`}>Advar</button>
                  <button onClick={() => resolve(r.id, "suspend")} className="btn-secondary text-xs text-destructive" data-testid={`suspend-${r.id}`}>Suspender</button>
                  <button onClick={() => resolve(r.id, "ban")} className="btn-primary text-xs" data-testid={`ban-${r.id}`}>Ban</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VerificationsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = async () => {
    try { const { data } = await api.get(`/admin/verifications?status=${status}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [status]);
  const decide = async (id, action) => {
    const reason = action === "reject" ? (prompt("Årsag til afvisning?", "") || "") : "";
    try { await api.post(`/admin/verifications/${id}/decide`, { action, reason }); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="verifications-tab">
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1 text-xs rounded-full ${status === s ? "bg-olive-500 text-white" : "bg-sand-100 text-brown-600"}`} data-testid={`verifs-filter-${s}`}>{s}</button>
        ))}
      </div>
      {items.length === 0 && <div className="card-soft p-10 text-center text-brown-500">Ingen verificeringer.</div>}
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((v) => (
          <div key={v.id} className="card-soft p-4" data-testid={`verif-row-${v.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium capitalize text-olive-700">{v.level}</div>
                <div className="text-xs text-brown-500">{v.user?.name || v.user?.email || v.user_id}</div>
                <div className="text-[10px] text-brown-400 mt-1">{new Date(v.created_at).toLocaleString("da-DK")}</div>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-brown-500">{v.status}</span>
            </div>
            <div className="mt-3">
              <img loading="lazy" decoding="async" src={photoUrl(v.upload_path)} alt="verificering" className="w-full aspect-square object-cover rounded-lg border border-sand-300" />
            </div>
            {v.status === "pending" && (
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => decide(v.id, "reject")} className="btn-secondary text-xs text-destructive" data-testid={`verif-reject-${v.id}`}>Afvis</button>
                <button onClick={() => decide(v.id, "approve")} className="btn-primary text-xs" data-testid={`verif-approve-${v.id}`}>Godkend</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SubscriptionsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("active");
  const load = async () => {
    try { const { data } = await api.get(`/admin/subscriptions?status=${status}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [status]);
  return (
    <div data-testid="subscriptions-tab">
      <div className="flex gap-2 mb-4">
        {["active", "inactive", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1 text-xs rounded-full ${status === s ? "bg-olive-500 text-white" : "bg-sand-100 text-brown-600"}`} data-testid={`subs-filter-${s}`}>{s}</button>
        ))}
      </div>
      {items.length === 0 && <div className="card-soft p-10 text-center text-brown-500">Ingen abonnementer.</div>}
      <div className="card-soft overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sand-100 text-left text-brown-500">
            <tr><Th>Bruger</Th><Th>Plan</Th><Th>Aktiv</Th><Th>Udløber</Th><Th>Sidste betaling</Th></tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-sand-200" data-testid={`sub-row-${u.id}`}>
                <Td>{u.name || u.email}</Td>
                <Td className="capitalize">{(u.subscription?.tier || "-").replaceAll("_", " ")}</Td>
                <Td>{u.subscription?.active ? "✓" : "—"}</Td>
                <Td>{u.subscription?.expires_at ? new Date(u.subscription.expires_at).toLocaleDateString("da-DK") : "—"}</Td>
                <Td>{u.last_transaction ? `${u.last_transaction.amount} ${u.last_transaction.currency?.toUpperCase() || ""} · ${u.last_transaction.status}` : "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Demo data tab ----------
function DemoDataPanel({ onRefresh }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [activityMsg, setActivityMsg] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/demo/status"); setStatus(data); }
    catch (e) { setErr(formatApiError(e)); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const seed = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/admin/demo/seed");
      await load();
      onRefresh?.();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cleanup = async () => {
    if (!(await confirmDialog({ title: "Slet alle demo-brugere?", body: "Rigtige brugere røres ikke. Handlingen kan ikke fortrydes.", tone: "danger", confirmText: "Ja, slet demo" }))) return;
    setBusy(true); setErr("");
    try {
      await api.delete("/admin/demo/cleanup");
      await load();
      onRefresh?.();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const seedActivity = async () => {
    setBusy(true); setErr(""); setActivityMsg("");
    try {
      const { data } = await api.post("/admin/demo/seed-activity");
      setActivityMsg(
        `Skabt: ${data.interests_created} interesser, ${data.matches_created} matches, ` +
        `${data.messages_created} beskeder, ${data.verifications_created} verificering(er), ` +
        `${data.reports_created} rapport(er).`
      );
      await load();
      onRefresh?.();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const repairPhotos = async (force = false) => {
    setBusy(true); setErr(""); setActivityMsg("");
    try {
      const { data } = await api.post("/admin/demo/repair-photos", { force });
      setActivityMsg(
        `Fotos ${force ? "tvangs-tildelt" : "repareret"}: ${data.fixed} genoploadet, ${data.already_ok} allerede OK.` +
        (data.junk_users_purged ? ` ${data.junk_users_purged} test-brugere fjernet.` : "") +
        (data.no_bundled_image?.length ? ` (Manglende bundlede billeder for demo_index: ${data.no_bundled_image.join(", ")})` : "")
      );
      toast.success(
        `${data.fixed} fotos ${force ? "tildelt unikt" : "genoploadet"}` +
        (data.junk_users_purged ? ` · ${data.junk_users_purged} test-brugere ryddet` : "")
      );
      await load();
      onRefresh?.();
    } catch (e) { setErr(formatApiError(e)); toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggle = async () => {
    if (!status) return;
    setBusy(true); setErr("");
    try {
      await api.post("/admin/demo/toggle", { enabled: !status.is_demo_enabled });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="card-soft p-6 text-center text-brown-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Indlæser demo-status…</div>;

  const progressPct = status.total > 0 ? Math.round((status.progress / status.total) * 100) : 0;
  const activity = status.activity || {};

  return (
    <div data-testid="demo-tab">
      {err && <div className="text-destructive text-sm mb-3">{err}</div>}
      {activityMsg && <div className="text-olive-700 text-sm mb-3 bg-olive-100 rounded p-2">{activityMsg}</div>}
      <div className="card-soft p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow">Demo-brugere</div>
            <h2 className="mt-2 font-serif text-2xl text-olive-700 flex items-center gap-2"><Sparkles className="h-5 w-5 text-gold-600" /> Realistisk demo-data</h2>
            <p className="mt-2 text-sm text-brown-600 max-w-2xl">
              Genererer 20 komplette test-profiler (10 mænd + 10 kvinder) med AI-genererede portrætter, spredt over Premium (8), Boost (4) og Gratis (8) niveauer. Brug knappen <em>Vis som bruger</em> under Brugere/Profiler for at træde ind i deres oplevelse.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={toggle}
              disabled={busy || status.seeded === 0}
              className={`rounded-full border px-4 py-2 text-sm font-medium ${status.is_demo_enabled ? "bg-olive-500 text-white border-olive-500" : "bg-white border-sand-300 text-brown-600"}`}
              data-testid="demo-toggle">
              {status.is_demo_enabled ? "Demo synlig" : "Demo skjult"}
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Seedede" value={status.seeded} />
          <MiniStat label="Forventet" value={status.expected} />
          <MiniStat label="Progress" value={`${progressPct}%`} accent={status.generating ? "gold" : ""} />
          <MiniStat label="Status" value={status.generating ? "Genererer…" : status.seeded > 0 ? "Klar" : "Tom"} />
        </div>

        {status.generating && (
          <div className="mt-4">
            <div className="h-2 bg-sand-200 rounded-full overflow-hidden">
              <div className="h-full bg-gold-500 transition-all" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="mt-2 text-xs text-brown-500">
              <Loader2 className="h-3 w-3 animate-spin inline mr-1" />
              Genererer profil {status.progress} af {status.total} — AI-billeder tager 10-30 sek pr. profil.
            </div>
          </div>
        )}

        {status.last_error && (
          <div className="mt-3 text-xs text-destructive bg-destructive/10 rounded p-2 whitespace-pre-wrap">
            Seneste fejl: {status.last_error}
          </div>
        )}

        <div className="mt-6 flex gap-2 flex-wrap">
          <button
            onClick={seed}
            disabled={busy || status.generating}
            className="btn-primary text-sm"
            data-testid="demo-seed-btn">
            {status.generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Kører…</> : <><Sparkles className="h-4 w-4" /> Generér demo-brugere</>}
          </button>
          <button
            onClick={() => repairPhotos(false)}
            disabled={busy || status.seeded === 0}
            className="rounded-full border border-gold-500/60 bg-gold-100/40 hover:bg-gold-100 text-gold-700 font-medium px-4 py-2 text-sm inline-flex items-center gap-1.5"
            data-testid="demo-repair-photos-btn"
            title="Genupload manglende demo-fotos fra bundlede filer (bruges efter deployment)">
            <Sparkles className="h-4 w-4" /> Reparér fotos
          </button>
          <button
            onClick={() => repairPhotos(true)}
            disabled={busy || status.seeded === 0}
            className="rounded-full border border-olive-500/60 bg-olive-100/40 hover:bg-olive-100 text-olive-700 font-medium px-4 py-2 text-sm inline-flex items-center gap-1.5"
            data-testid="demo-force-repair-photos-btn"
            title="Tildel HVER demo-profil sit unikke bundlede portræt (fixer dublerede/forkerte billeder) + rydder test-brugere">
            <Sparkles className="h-4 w-4" /> Tildel unikke fotos (tving)
          </button>
          <button
            onClick={cleanup}
            disabled={busy || status.seeded === 0}
            className="btn-secondary text-sm text-destructive"
            data-testid="demo-cleanup-btn">
            <Trash2 className="h-4 w-4" /> Slet alle demo-brugere ({status.seeded})
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-sand-200 text-xs text-brown-500">
          <div className="font-medium text-brown-600 mb-1">Fordeling</div>
          <ul className="space-y-1">
            <li>• <strong>8 Premium</strong> betalende (fuld pakke, 6 mdr aktiv, verificeret)</li>
            <li>• <strong>4 Boost</strong> (24t priority discovery aktiv, verificeret)</li>
            <li>• <strong>8 Gratis</strong> brugere uden abonnement</li>
            <li>• Geografisk spredt: København, London, Malmö, Oslo, Berlin, Aarhus, Manchester, Stockholm, Bergen</li>
          </ul>
        </div>
      </div>

      {/* Activity panel */}
      <div className="card-soft p-6 mt-6">
        <div className="eyebrow">Live aktivitet</div>
        <h2 className="mt-2 font-serif text-2xl text-olive-700 flex items-center gap-2">
          <Zap className="h-5 w-5 text-gold-600" /> Sammenkobl demo-brugere
        </h2>
        <p className="mt-2 text-sm text-brown-600 max-w-2xl">
          Skab et levende feed: 10 interesser sendes mellem demo-brugere, ~5 accepteres til matches
          med realistiske Danish chat-tråde, 2 afvises, 1 selfie-verificering venter i køen, og 1
          rapport bliver klar til moderation. Idempotent — kan køres flere gange uden dubletter.
        </p>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          <MiniStat label="Interesser" value={activity.interests || 0} />
          <MiniStat label="Matches" value={activity.matches || 0} />
          <MiniStat label="Beskeder" value={activity.messages || 0} />
          <MiniStat label="Verifikationer" value={activity.verifications_pending || 0} accent={activity.verifications_pending > 0 ? "gold" : ""} />
          <MiniStat label="Rapporter" value={activity.reports_pending || 0} accent={activity.reports_pending > 0 ? "gold" : ""} />
        </div>

        <div className="mt-6 flex gap-2 flex-wrap">
          <button
            onClick={seedActivity}
            disabled={busy || status.seeded < 10}
            className="btn-primary text-sm"
            data-testid="demo-seed-activity-btn">
            <Zap className="h-4 w-4" /> Sammenkobl demo-brugere
          </button>
        </div>
        {status.seeded < 10 && (
          <div className="mt-3 text-xs text-brown-500">Seed demo-brugere først (min. 10 nødvendige).</div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className={`rounded-xl p-3 ${accent === "gold" ? "bg-gold-100/60 border border-gold-500/30" : "bg-sand-100"}`}>
      <div className="text-[10px] uppercase tracking-widest text-brown-500">{label}</div>
      <div className="font-serif text-2xl text-olive-700">{value}</div>
    </div>
  );
}


// ---------- Team / Access panel: moderators, manual users, premium invites ----------
function TeamPanel() {
  return (
    <div className="space-y-8" data-testid="team-panel">
      <StaffSection />
      <CreateUserSection />
      <PremiumInvitesSection />
    </div>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="card-soft p-6">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-olive-100 text-olive-700 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-serif text-xl text-olive-700">{title}</h3>
          {subtitle && <p className="text-sm text-brown-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function StaffSection() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "moderator" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/staff"); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/staff", form);
      toast.success("Medarbejder oprettet");
      setForm({ name: "", email: "", password: "", role: "moderator" });
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    const ok = await confirmDialog({
      title: "Fjern adgang?",
      body: `${u.name || u.email} mister medarbejder-adgang og bliver en almindelig bruger.`,
      confirmText: "Fjern adgang", cancelText: "Annullér",
    });
    if (!ok) return;
    try { await api.delete(`/admin/staff/${u.id}`); toast.success("Adgang fjernet"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <SectionCard icon={ShieldPlus} title="Moderatorer & administratorer" subtitle="Moderatorer har fuld adgang til at styre hele siden — præcis som en administrator.">
      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3" data-testid="staff-create-form">
        <input className="field" placeholder="Navn" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
        <input className="field" type="email" placeholder="E-mail" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
        <input className="field" type="password" placeholder="Adgangskode (min. 8 tegn)" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
        <select className="field" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role">
          <option value="moderator">Moderator</option>
          <option value="admin">Administrator</option>
        </select>
        <div className="sm:col-span-2">
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="staff-create-btn">
            <UserPlus className="h-4 w-4" /> {busy ? "Opretter…" : "Tilføj medarbejder"}
          </button>
        </div>
      </form>

      <ul className="mt-6 divide-y divide-sand-200" data-testid="staff-list">
        {items.map((u) => (
          <li key={u.id} className="flex items-center justify-between py-3" data-testid={`staff-row-${u.id}`}>
            <div className="min-w-0">
              <div className="font-medium text-olive-700 truncate flex items-center gap-2">
                {u.name || "—"}
                <span className={`text-[9px] uppercase tracking-widest rounded px-1.5 py-0.5 ${u.role === "admin" ? "bg-gold-100 text-gold-700" : "bg-olive-100 text-olive-700"}`}>{u.role}</span>
                {u.is_super_admin && <span className="text-[9px] uppercase tracking-widest bg-sand-200 text-brown-600 rounded px-1.5 py-0.5">Hoved</span>}
              </div>
              <div className="text-xs text-brown-500 break-all">{u.email}</div>
            </div>
            {!u.is_super_admin && (
              <button onClick={() => remove(u)} className="inline-flex items-center gap-1 text-xs text-destructive hover:bg-destructive/10 rounded-full px-3 py-1.5" data-testid={`staff-remove-${u.id}`}>
                <Trash2 className="h-3.5 w-3.5" /> Fjern
              </button>
            )}
          </li>
        ))}
        {items.length === 0 && <li className="py-6 text-center text-brown-500 text-sm">Ingen medarbejdere endnu.</li>}
      </ul>
    </SectionCard>
  );
}

function CreateUserSection() {
  const [form, setForm] = useState({ name: "", email: "", password: "", make_premium: false, premium_months: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setResult(null);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password ? form.password : null,
        make_premium: form.make_premium,
        premium_months: form.make_premium && form.premium_months ? Number(form.premium_months) : null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/admin/users", payload);
      setResult(data);
      toast.success(`Bruger ${data.email} oprettet`);
      setForm({ name: "", email: "", password: "", make_premium: false, premium_months: "" });
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <SectionCard icon={UserPlus} title="Opret bruger manuelt" subtitle="Tilføj en bruger direkte. Lad adgangskoden stå tom for at sende et aktiveringslink, som brugeren selv sætter kode med.">
      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3" data-testid="create-user-form">
        <input className="field" placeholder="Navn" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="createuser-name" />
        <input className="field" type="email" placeholder="E-mail" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="createuser-email" />
        <input className="field" type="text" placeholder="Adgangskode (valgfri – tom = aktiveringslink)" minLength={0} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="createuser-password" />
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-brown-600">
            <input type="checkbox" checked={form.make_premium} onChange={(e) => setForm({ ...form, make_premium: e.target.checked })} data-testid="createuser-premium" />
            Gør premium
          </label>
          {form.make_premium && (
            <select className="field !py-1.5" value={form.premium_months} onChange={(e) => setForm({ ...form, premium_months: e.target.value })} data-testid="createuser-premium-duration">
              <option value="">Livstid</option>
              <option value="1">1 måned</option>
              <option value="3">3 måneder</option>
              <option value="6">6 måneder</option>
              <option value="12">12 måneder</option>
            </select>
          )}
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="createuser-btn">
            <UserPlus className="h-4 w-4" /> {busy ? "Opretter…" : "Opret bruger"}
          </button>
        </div>
      </form>

      {result && (
        <div className="mt-5 rounded-2xl border border-olive-500/30 bg-olive-100/40 p-4" data-testid="createuser-result">
          <div className="text-sm text-olive-700 font-medium">Bruger oprettet: {result.email}{result.premium ? " · Premium ✓" : ""}</div>
          {result.activation_url && <CopyLink label="Aktiveringslink (send til brugeren)" url={result.activation_url} testid="createuser-activation-link" />}
        </div>
      )}
    </SectionCard>
  );
}

function PremiumInvitesSection() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ label: "", single_use: true, max_uses: "", premium_months: "", expires_days: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/premium-invites?origin_url=${encodeURIComponent(window.location.origin)}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        label: form.label,
        single_use: form.single_use,
        max_uses: !form.single_use && form.max_uses ? Number(form.max_uses) : null,
        premium_months: form.premium_months ? Number(form.premium_months) : null,
        expires_days: form.expires_days ? Number(form.expires_days) : null,
        origin_url: window.location.origin,
      };
      await api.post("/admin/premium-invites", payload);
      toast.success("Invitationslink oprettet");
      setForm({ label: "", single_use: true, max_uses: "", premium_months: "", expires_days: "" });
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const revoke = async (inv) => {
    const ok = await confirmDialog({
      title: "Tilbagekald link?",
      body: "Linket kan derefter ikke længere bruges.",
      confirmText: "Tilbagekald", cancelText: "Annullér",
    });
    if (!ok) return;
    try { await api.delete(`/admin/premium-invites/${inv.id}`); toast.success("Link tilbagekaldt"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <SectionCard icon={LinkIcon} title="Premium-invitationslinks" subtitle="Generér et link — alle der tilmelder sig via linket bliver automatisk premium.">
      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3" data-testid="invite-create-form">
        <input className="field" placeholder="Navn/etiket (fx 'VIP kampagne')" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="invite-label" />
        <select className="field" value={form.single_use ? "single" : "multi"} onChange={(e) => setForm({ ...form, single_use: e.target.value === "single" })} data-testid="invite-usetype">
          <option value="single">Engangslink (1 person)</option>
          <option value="multi">Flergangslink (mange personer)</option>
        </select>
        {!form.single_use && (
          <input className="field" type="number" min="1" placeholder="Maks. antal (tom = ubegrænset)" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="invite-maxuses" />
        )}
        <select className="field" value={form.premium_months} onChange={(e) => setForm({ ...form, premium_months: e.target.value })} data-testid="invite-duration">
          <option value="">Premium: Livstid</option>
          <option value="1">Premium: 1 måned</option>
          <option value="3">Premium: 3 måneder</option>
          <option value="6">Premium: 6 måneder</option>
          <option value="12">Premium: 12 måneder</option>
        </select>
        <input className="field" type="number" min="1" placeholder="Link udløber om (dage, tom = aldrig)" value={form.expires_days} onChange={(e) => setForm({ ...form, expires_days: e.target.value })} data-testid="invite-expires" />
        <div className="sm:col-span-2">
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="invite-create-btn">
            <LinkIcon className="h-4 w-4" /> {busy ? "Opretter…" : "Generér link"}
          </button>
        </div>
      </form>

      <ul className="mt-6 space-y-3" data-testid="invite-list">
        {items.map((inv) => (
          <li key={inv.id} className="rounded-2xl border border-sand-200 p-4" data-testid={`invite-row-${inv.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-medium text-olive-700 flex items-center gap-2">
                  <Crown className="h-4 w-4 text-gold-600" />
                  {inv.label || "Premium-link"}
                  <span className={`text-[9px] uppercase tracking-widest rounded px-1.5 py-0.5 ${inv.active ? "bg-olive-100 text-olive-700" : "bg-sand-200 text-brown-500"}`}>
                    {inv.active ? "Aktiv" : "Inaktiv"}
                  </span>
                </div>
                <div className="text-xs text-brown-500 mt-1">
                  {inv.lifetime ? "Livstids-premium" : `${inv.premium_months} mdr. premium`}
                  {" · "}{inv.max_uses === null ? "ubegrænset brug" : `${inv.uses}/${inv.max_uses} brugt`}
                  {inv.expires_at ? ` · udløber ${new Date(inv.expires_at).toLocaleDateString("da-DK")}` : ""}
                </div>
              </div>
              {inv.active && (
                <button onClick={() => revoke(inv)} className="inline-flex items-center gap-1 text-xs text-destructive hover:bg-destructive/10 rounded-full px-3 py-1.5" data-testid={`invite-revoke-${inv.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Tilbagekald
                </button>
              )}
            </div>
            {inv.active && <CopyLink label="" url={inv.url} testid={`invite-link-${inv.id}`} />}
          </li>
        ))}
        {items.length === 0 && <li className="py-6 text-center text-brown-500 text-sm">Ingen invitationslinks endnu.</li>}
      </ul>
    </SectionCard>
  );
}

function CopyLink({ label, url, testid }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Kopieret"); }
    catch { toast.error("Kunne ikke kopiere"); }
  };
  return (
    <div className="mt-2">
      {label && <div className="text-xs text-brown-500 mb-1">{label}</div>}
      <div className="flex items-center gap-2">
        <input readOnly value={url} className="field !py-2 text-xs flex-1 bg-sand-50" data-testid={testid} onFocus={(e) => e.target.select()} />
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded-full border border-olive-500/40 text-olive-700 hover:bg-olive-100/60 px-3 py-2 text-xs shrink-0" data-testid={`${testid}-copy`}>
          <Copy className="h-3.5 w-3.5" /> Kopiér
        </button>
      </div>
    </div>
  );
}
